import { WebLocale } from '@/enums/web-locale';
import { MonetizationType, PresentationType, SportObjectType, SportOffer, SportType } from '@/@types/graphql-types';

import type { SportLocale } from '@/enums/sports';

interface CompetitionValue {
	fullPath: string;
	id: string;
	objectId: number;
	name: string;
	sport: SportType;
}

/**
 * Temporary file to hold hard coded values relating to AllSports.
 *
 * All these values will eventually be returned by the API an we will delete this file.
 */

export const TOP_COMPETITIONS: Partial<Record<SportLocale | 'default', string[]>> = {
	[WebLocale.us]: [
		'scn-bkb-8331232-v2', // NBA
		'scn-afb-7440571', // NFL
		'scn-ice-17698463-v2', // NHL
		'scn-foo-133', // UEFA Champions League
		'scn-foo-2164036', // LaLiga
	],
	[WebLocale.ca]: [
		'scn-bkb-8331232-v2', // NBA
		'scn-afb-7440571', // NFL
		'scn-ice-17698463-v2', // NHL
		'scn-foo-133', // UEFA Champions League
		'scn-foo-2164036', // LaLiga
	],
	default: [
		'scn-foo-133', // UEFA Champions League
		'scn-foo-1952', // Bundesliga
		'scn-foo-4347', // Premier League
		'scn-foo-2175137', // Serie A
		// 'scn-foo-2167071', // Ligue 1
		'scn-foo-2164036', // LaLiga
	],
};

export const COMPETITION_VALUES: Record<SportLocale, readonly CompetitionValue[]> = {
	[WebLocale.de]: [
		{
			fullPath: '/de/fussball/World-Cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'World Cup',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/UEFA-Champions-League',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/dfb-pokal',
			id: 'scn-foo-1262344',
			objectId: 1262344,
			name: 'DFB Pokal',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/motorsport/Formel-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formel 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/de/fussball/UEFA-Europa-Conference-League',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/3-Liga',
			id: 'scn-foo-2874680',
			objectId: 2874680,
			name: '3. Liga',
			sport: SportType.Football,
		},
		{
			fullPath: '/de/fussball/LaLiga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.uk]: [
		{
			fullPath: '/uk/football/World-Cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'World Cup',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/uk/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/uk/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.es]: [
		{
			fullPath: '/es/futbol/copa-mundial',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Copa Mundial',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/futbol/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/es/motor/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
	],
	[WebLocale.it]: [
		{
			fullPath: '/it/calcio/coppa-del-mondo',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Coppa Del Mondo',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/calcio/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/it/motosport/formula-uno',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula Uno',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/it/calcio/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.fr]: [
		{
			fullPath: '/fr/foot/coupe-du-monde',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Coupe Du Monde',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/ligue-des-champions-uefa',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'Ligue des Champions UEFA',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/ligue-europa-conference',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/foot/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/fr/sport-mecanique/formule-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formule 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/fr/foot/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.us]: [
		{
			fullPath: '/us/soccer/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/basketball/nba',
			id: 'scn-bkb-8331232-v2',
			objectId: 8331232,
			name: 'NBA',
			sport: SportType.Basketball,
		},
		{
			fullPath: '/us/ice-hockey/nhl',
			id: 'scn-ice-17698463-v2',
			objectId: 8331232,
			name: 'NHL',
			sport: SportType.IceHockey,
		},
		{
			fullPath: '/us/football/nfl',
			id: 'scn-afb-7440571',
			objectId: 7440571,
			name: 'NFL',
			sport: SportType.AmericanFootball,
		},
		{
			fullPath: '/us/soccer/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'World Cup',
			sport: SportType.Football,
		},

		{
			fullPath: '/us/football/ncaa-regular-season',
			id: 'scn-afb-7605686',
			objectId: 7605686,
			name: 'NCAA Regular Season',
			sport: SportType.AmericanFootball,
		},
		{
			fullPath: '/us/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/us/soccer/UEFA-Champions-League',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/major-league-soccer',
			id: 'scn-foo-7925167',
			objectId: 7925167,
			name: 'MLS',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier League',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/tennis/us-open-men-singles',
			id: 'scn-ten-6645809',
			objectId: 6645809,
			name: 'US Open Men Singles',
			sport: SportType.Tennis,
		},
		{
			fullPath: '/us/soccer/2nd-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/dfb-pokal',
			id: 'scn-foo-1262344',
			objectId: 1262344,
			name: 'DFB Pokal',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/us/soccer/LaLiga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.ca]: [
		{
			fullPath: '/ca/soccer/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'World Cup',
			sport: SportType.Football,
		},
		{
			fullPath: '/ca/soccer/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/ca/basketball/nba',
			id: 'scn-bkb-8331232-v2',
			objectId: 8331232,
			name: 'NBA',
			sport: SportType.Basketball,
		},
		{
			fullPath: '/ca/ice-hockey/nhl',
			id: 'scn-ice-17698463-v2',
			objectId: 8331232,
			name: 'NHL',
			sport: SportType.IceHockey,
		},
		{
			fullPath: '/ca/football/nfl',
			id: 'scn-afb-7440571',
			objectId: 7440571,
			name: 'NFL',
			sport: SportType.AmericanFootball,
		},
	],
	[WebLocale.tr]: [
		{
			fullPath: '/tr/futbol/dunya-kupasi',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Dünya Kupası',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/premier-lig',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'Premier Lig',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/uefa-sampiyonlar-ligi',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Şampiyonlar Ligi',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/uefa-avrupa-konferans-ligi',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Avrupa Konferans Ligi',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'LaLiga',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: 'Ligue 1',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'Serie A',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/futbol/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/tr/motorsporları/formül-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formül 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/tr/futbol/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.qa]: [
		{
			fullPath: '/qa/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/qa/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/qa/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.ae]: [
		{
			fullPath: '/ae/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/ae/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/ae/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.kw]: [
		{
			fullPath: '/kw/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/kw/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/kw/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.sa]: [
		{
			fullPath: '/sa/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/sa/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/sa/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.bh]: [
		{
			fullPath: '/bh/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/bh/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/bh/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.om]: [
		{
			fullPath: '/om/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/om/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/om/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.eg]: [
		{
			fullPath: '/eg/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/eg/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.jo]: [
		{
			fullPath: '/jo/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/eg/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/eg/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.iq]: [
		{
			fullPath: '/iq/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'كاس العالم',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/premier-league',
			id: 'scn-foo-4347',
			objectId: 4347,
			name: 'الدوري الممتاز الإنجليزي',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/uefa-champions-league',
			id: 'scn-foo-133',
			objectId: 133,
			name: 'UEFA Champions League',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/uefa-europa-conference-league',
			id: 'scn-foo-170',
			objectId: 170,
			name: 'UEFA Europa Conference League',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/Bundesliga',
			id: 'scn-foo-1952',
			objectId: 1952,
			name: 'الدوري الالماني',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/2-bundesliga',
			id: 'scn-foo-2885112',
			objectId: 2885112,
			name: '2. Bundesliga',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/laliga',
			id: 'scn-foo-2164036',
			objectId: 2164036,
			name: 'الدوري الاسباني',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/ligue-1',
			id: 'scn-foo-2167071',
			objectId: 2167071,
			name: '1الدوري',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/ligue-2',
			id: 'scn-foo-4128028',
			objectId: 4128028,
			name: 'Ligue 2',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/serie-a',
			id: 'scn-foo-2175137',
			objectId: 2175137,
			name: 'الدوري الايطالي',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/football/serie-b',
			id: 'scn-foo-3994983',
			objectId: 3994983,
			name: 'Serie B',
			sport: SportType.Football,
		},
		{
			fullPath: '/iq/motorsport/formula-1',
			id: 'scn-for-1422294',
			objectId: 1422294,
			name: 'Formula 1',
			sport: SportType.FormulaRacing,
		},
		{
			fullPath: '/iq/football/laliga-2',
			id: 'scn-foo-2882338',
			objectId: 2882338,
			name: 'LaLiga 2',
			sport: SportType.Football,
		},
	],
	[WebLocale.nl]: [
		{
			fullPath: '/nl/football/world-cup',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'World Cup',
			sport: SportType.Football,
		},
	],
	[WebLocale.ar]: [
		{
			fullPath: '/ar/futbol/copa-mundial',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Copa Mundial',
			sport: SportType.Football,
		},
	],
	[WebLocale.mx]: [
		{
			fullPath: '/mx/futbol/copa-mundial',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Copa Mundial',
			sport: SportType.Football,
		},
	],
	[WebLocale.br]: [
		{
			fullPath: '/br/futebol/campeonato-do-mundo',
			id: 'scn-foo-26249831',
			objectId: 26249831,
			name: 'Copa Mundial',
			sport: SportType.Football,
		},
	],
} as const;
